import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Grid,
  Tooltip,
  CircularProgress,
  FormHelperText,
  Divider
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ImageIcon from '@mui/icons-material/Image';
import CategoryIcon from '@mui/icons-material/Category';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import toast from 'react-hot-toast';
import { getApiUrl } from '../config';
import { parseTags, tagsToString } from '../utils/helpers';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

function Blog() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    category_id: '',
    author: 'مرکز مشاوره نارون',
    tags: [],
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    featured_image: null
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const res = await fetch(getApiUrl('/api/blog'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setPosts(data);
      } else {
        toast.error('خطا در دریافت پست‌ها');
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('خطا در دریافت پست‌ها');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(getApiUrl('/api/categories'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'عنوان پست الزامی است';
    }
    
    if (!formData.content.trim()) {
      newErrors.content = 'محتوای پست الزامی است';
    }
    
    if (!formData.category_id) {
      newErrors.category_id = 'انتخاب دسته‌بندی الزامی است';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('لطفاً خطاهای فرم را برطرف کنید');
      return;
    }

    try {
      setFormLoading(true);
      
      // Upload image first if exists
      let imageUrl = null;
      if (formData.featured_image && formData.featured_image instanceof File) {
        imageUrl = await handleImageUpload(formData.featured_image);
        if (!imageUrl) {
          toast.error('خطا در آپلود تصویر');
          return;
        }
      }
    
      // Prepare JSON data for blog post
      const postData = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        excerpt: formData.excerpt.trim(),
        category_id: parseInt(formData.category_id),
        author: formData.author.trim(),
        tags: formData.tags,
        meta_title: formData.meta_title.trim(),
        meta_description: formData.meta_description.trim(),
        meta_keywords: formData.meta_keywords.trim(),
        featured_image: imageUrl || formData.featured_image
      };

      const url = editingPost 
        ? getApiUrl(`/api/blog/${editingPost.id}`)
        : getApiUrl('/api/blog');
      
      const method = editingPost ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(postData)
      });

      if (res.ok) {
        const result = await res.json();
        toast.success(editingPost ? 'پست با موفقیت ویرایش شد' : 'پست با موفقیت ایجاد شد');
        setOpenDialog(false);
        resetForm();
        fetchPosts();
      } else {
        const error = await res.json();
        toast.error(error.error || 'خطا در ذخیره پست');
      }
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error('خطا در ذخیره پست');
    } finally {
      setFormLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append('image', file);

    try {
      const res = await fetch(getApiUrl('/api/blog/upload-image'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        return data.url;
      } else {
        throw new Error('خطا در آپلود تصویر');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('خطا در آپلود تصویر');
      return null;
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    
    setFormData({
      title: post.title || '',
      content: post.content || '',
      excerpt: post.excerpt || '',
      category_id: post.category_id || '',
      author: post.author || 'مرکز مشاوره نارون',
      tags: parseTags(post.tags),
      meta_title: post.meta_title || '',
      meta_description: post.meta_description || '',
      meta_keywords: post.meta_keywords || '',
      featured_image: post.featured_image || post.image || null
    });
    setImagePreview(post.featured_image || post.image ? `${getApiUrl('')}${post.featured_image || post.image}` : null);
    setErrors({});
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این پست اطمینان دارید؟')) return;

    try {
      const res = await fetch(getApiUrl(`/api/blog/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (res.ok) {
        toast.success('پست با موفقیت حذف شد');
        fetchPosts();
      } else {
        toast.error('خطا در حذف پست');
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('خطا در حذف پست');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      category_id: '',
      author: 'مرکز مشاوره نارون',
      tags: [],
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
      featured_image: null
    });
    setImagePreview(null);
    setEditingPost(null);
    setErrors({});
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('حجم فایل نباید بیشتر از 5 مگابایت باشد');
        return;
      }
      setFormData({ ...formData, featured_image: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleTagsChange = (e) => {
    const tags = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
    setFormData({ ...formData, tags });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fa-IR');
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'بدون دسته‌بندی';
  };

  const truncateText = (text, maxLength = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            مدیریت بلاگ
          </Typography>
          <Typography variant="body2" color="text.secondary">
            مدیریت مقالات و محتوای وبلاگ
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            resetForm();
            setOpenDialog(true);
          }}
          sx={{ minWidth: 150 }}
        >
          افزودن پست جدید
        </Button>
      </Box>

      {/* Posts Table */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>عنوان</TableCell>
                    <TableCell>دسته‌بندی</TableCell>
                    <TableCell>نویسنده</TableCell>
                    <TableCell>تاریخ ایجاد</TableCell>
                    <TableCell>عملیات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {posts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          هیچ پستی یافت نشد
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    posts.map((post) => (
                      <TableRow key={post.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>
                              {post.title}
                            </Typography>
                            {post.excerpt && (
                              <Typography variant="body2" color="text.secondary">
                                {truncateText(post.excerpt, 80)}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            icon={<CategoryIcon />}
                            label={getCategoryName(post.category_id)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {post.author || 'مرکز مشاوره نارون'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CalendarTodayIcon sx={{ fontSize: 16, mr: 0.5 }} />
                            <Typography variant="body2">
                              {formatDate(post.created_at)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            
                            <Tooltip title="ویرایش">
                              <IconButton 
                                size="small"
                                onClick={() => handleEdit(post)}
                                color="primary"
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="حذف">
                              <IconButton 
                                size="small"
                                onClick={() => handleDelete(post.id)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Blog Form Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle>
          <Typography variant="h6">
            {editingPost ? 'ویرایش پست' : 'افزودن پست جدید'}
          </Typography>
        </DialogTitle>
        
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <Grid container spacing={3}>
                {/* Right Side - Main Content (8 columns) */}
                <Grid item xs={12} md={8}>
                  <Box sx={{ pr: 2 }}>
                    {/* Title */}
                    <TextField
                      fullWidth
                      label="عنوان پست *"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      error={!!errors.title}
                      helperText={errors.title}
                      required
                      size="medium"
                      sx={{ mb: 3 }}
                    />

                    {/* Author */}
                    <TextField
                      fullWidth
                      label="نویسنده"
                      value={formData.author}
                      onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                      placeholder="مرکز مشاوره نارون"
                      size="medium"
                      sx={{ mb: 3 }}
                    />

                    {/* Category */}
                    <FormControl fullWidth error={!!errors.category_id} required size="medium" sx={{ mb: 3 }}>
                      <InputLabel>دسته‌بندی *</InputLabel>
                      <Select
                        value={formData.category_id}
                        onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                        label="دسته‌بندی *"
                      >
                        {categories.map((category) => (
                          <MenuItem key={category.id} value={category.id}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.category_id && (
                        <FormHelperText>{errors.category_id}</FormHelperText>
                      )}
                    </FormControl>

                    {/* Tags */}
                    <TextField
                      fullWidth
                      label="برچسب‌ها"
                      value={tagsToString(formData.tags)}
                      onChange={handleTagsChange}
                      placeholder="شنوایی, سمعک, سلامت"
                      helperText="برچسب‌ها را با کاما جدا کنید"
                      size="medium"
                      sx={{ mb: 3 }}
                    />

                    {/* Excerpt */}
                    <TextField
                      fullWidth
                      label="خلاصه مقاله"
                      value={formData.excerpt}
                      onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                      multiline
                      rows={3}
                      placeholder="خلاصه کوتاهی از مقاله..."
                      helperText="این خلاصه در لیست مقالات نمایش داده می‌شود"
                      size="medium"
                      sx={{ mb: 3 }}
                    />

                    {/* Content */}
                    <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
                      محتوای مقاله *
                    </Typography>
                    <Box sx={{ 
                      border: errors.content ? '2px solid #d32f2f' : '1px solid #e0e0e0', 
                      borderRadius: 1,
                      '& .ql-container': {
                        minHeight: '300px'
                      }
                    }}>
                      <ReactQuill
                        value={formData.content}
                        onChange={(value) => setFormData({ ...formData, content: value })}
                        theme="snow"
                        style={{ height: '300px' }}
                        modules={{
                          toolbar: [
                            [{ 'header': [1, 2, 3, false] }],
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                            [{ 'color': [] }, { 'background': [] }],
                            [{ 'align': [] }],
                            ['link', 'image'],
                            ['clean']
                          ]
                        }}
                      />
                    </Box>
                    {errors.content && (
                      <FormHelperText error sx={{ mt: 1 }}>{errors.content}</FormHelperText>
                    )}
                  </Box>
                </Grid>

                {/* Left Side - SEO & Image (4 columns) */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ pl: 1 }}>
                    {/* SEO Section */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
                        تنظیمات SEO
                      </Typography>
                      
                      <TextField
                        fullWidth
                        label="عنوان SEO"
                        value={formData.meta_title}
                        onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                        placeholder={formData.title || 'عنوان مقاله'}
                        helperText="اگر خالی باشد، عنوان مقاله استفاده می‌شود"
                        size="medium"
                        sx={{ mb: 2 }}
                      />

                      <TextField
                        fullWidth
                        label="توضیحات SEO"
                        value={formData.meta_description}
                        onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                        multiline
                        rows={3}
                        placeholder={formData.excerpt || 'توضیحات کوتاه مقاله...'}
                        helperText="اگر خالی باشد، خلاصه مقاله استفاده می‌شود"
                        size="medium"
                        sx={{ mb: 2 }}
                      />

                      <TextField
                        fullWidth
                        label="کلمات کلیدی SEO"
                        value={formData.meta_keywords}
                        onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                        placeholder="کلمات کلیدی را با کاما جدا کنید"
                        helperText="کلمات کلیدی را با کاما جدا کنید"
                        size="medium"
                      />
                    </Box>

                    {/* Image Upload Section */}
                    <Box>
                      <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
                        تصویر شاخص
                      </Typography>
                      
                      <Box sx={{ 
                        border: '2px dashed #e0e0e0', 
                        borderRadius: 2, 
                        p: 2, 
                        textAlign: 'center',
                        backgroundColor: '#fafafa',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: '#f5f5f5'
                        }
                      }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <ImageIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                          <Button
                            variant="outlined"
                            component="label"
                            startIcon={<ImageIcon />}
                            size="small"
                            sx={{ minWidth: 120 }}
                          >
                            انتخاب تصویر
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageChange}
                              style={{ display: 'none' }}
                            />
                          </Button>
                          <Typography variant="caption" color="text.secondary">
                            حداکثر حجم: 5 مگابایت
                          </Typography>
                        </Box>
                      </Box>

                      {imagePreview && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            style={{ 
                              maxWidth: '100%', 
                              maxHeight: '150px',
                              borderRadius: '8px',
                              border: '1px solid #ddd',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }} 
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={() => setOpenDialog(false)}
              disabled={formLoading}
            >
              لغو
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={formLoading}
              startIcon={formLoading ? <CircularProgress size={20} /> : null}
            >
              {formLoading ? 'در حال ذخیره...' : (editingPost ? 'ویرایش' : 'افزودن')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Blog; 